// =============================================================================
// Module Core - useFavoritesStore
// Gestion des favoris avec optimistic updates
// IMPORTANT: Ce store n'importe PAS supabase directement
// =============================================================================

import { create } from 'zustand';
import type { Favorite } from '../types';
import * as favoriteRepo from '../data/favoriteRepository';

// ─────────────────────────────────────────────────────────────────────────────
// STATE
// ─────────────────────────────────────────────────────────────────────────────
interface FavoritesState {
  favorites: Favorite[];
  loading: boolean;
  error: string | null;
  
  // Current context
  userId: string | null;
  orgId: string | null;
  
  // Actions
  setContext: (userId: string | null, orgId: string | null) => void;
  loadFavorites: () => Promise<void>;
  addFavorite: (projectId: string, projectName: string) => Promise<void>;
  removeFavorite: (projectId: string) => Promise<void>;
  toggleFavorite: (projectId: string, projectName: string) => Promise<void>;
  isFavorite: (projectId: string) => boolean;
  clearFavorites: () => void;
}

// ─────────────────────────────────────────────────────────────────────────────
// STORE
// ─────────────────────────────────────────────────────────────────────────────
export const useFavoritesStore = create<FavoritesState>((set, get) => ({
  favorites: [],
  loading: false,
  error: null,
  userId: null,
  orgId: null,

  // ─────────────────────────────────────────────────────────────────────────
  // SET CONTEXT
  // ─────────────────────────────────────────────────────────────────────────
  setContext: (userId, orgId) => {
    const state = get();
    // Only update if context changed
    if (state.userId !== userId || state.orgId !== orgId) {
      set({ userId, orgId, favorites: [], loading: false, error: null });
    }
  },

  // ─────────────────────────────────────────────────────────────────────────
  // LOAD FAVORITES
  // ─────────────────────────────────────────────────────────────────────────
  loadFavorites: async () => {
    const { userId, orgId } = get();
    if (!userId || !orgId) {
      set({ favorites: [], loading: false });
      return;
    }

    set({ loading: true, error: null });

    try {
      const favorites = await favoriteRepo.fetchFavorites(userId, orgId);
      set({ favorites, loading: false });
    } catch (err) {
      set({ error: 'Failed to load favorites', loading: false });
    }
  },

  // ─────────────────────────────────────────────────────────────────────────
  // ADD FAVORITE (optimistic)
  // ─────────────────────────────────────────────────────────────────────────
  addFavorite: async (projectId, projectName) => {
    const { userId, orgId, favorites } = get();
    if (!userId || !orgId) return;

    // Optimistic update
    const tempId = `temp-${Date.now()}`;
    const newFavorite: Favorite = {
      id: tempId,
      projectId,
      projectName,
    };
    set({ favorites: [...favorites, newFavorite] });

    try {
      const result = await favoriteRepo.addFavorite(userId, orgId, projectId);
      
      if (result) {
        // Update with real ID
        set({
          favorites: get().favorites.map((f) =>
            f.id === tempId ? { ...f, id: result.id } : f
          ),
        });
      } else {
        // Rollback
        set({ favorites: get().favorites.filter((f) => f.id !== tempId) });
      }
    } catch {
      // Rollback
      set({ favorites: get().favorites.filter((f) => f.id !== tempId) });
    }
  },

  // ─────────────────────────────────────────────────────────────────────────
  // REMOVE FAVORITE (optimistic)
  // ─────────────────────────────────────────────────────────────────────────
  removeFavorite: async (projectId) => {
    const { userId, favorites } = get();
    if (!userId) return;

    const favoriteToRemove = favorites.find((f) => f.projectId === projectId);
    if (!favoriteToRemove) return;

    // Optimistic update
    set({ favorites: favorites.filter((f) => f.projectId !== projectId) });

    try {
      const success = await favoriteRepo.removeFavorite(userId, projectId);
      
      if (!success) {
        // Rollback
        set({ favorites: [...get().favorites, favoriteToRemove] });
      }
    } catch {
      // Rollback
      set({ favorites: [...get().favorites, favoriteToRemove] });
    }
  },

  // ─────────────────────────────────────────────────────────────────────────
  // TOGGLE FAVORITE
  // ─────────────────────────────────────────────────────────────────────────
  toggleFavorite: async (projectId, projectName) => {
    const { isFavorite, addFavorite, removeFavorite } = get();
    
    if (isFavorite(projectId)) {
      await removeFavorite(projectId);
    } else {
      await addFavorite(projectId, projectName);
    }
  },

  // ─────────────────────────────────────────────────────────────────────────
  // IS FAVORITE
  // ─────────────────────────────────────────────────────────────────────────
  isFavorite: (projectId) => {
    return get().favorites.some((f) => f.projectId === projectId);
  },

  // ─────────────────────────────────────────────────────────────────────────
  // CLEAR
  // ─────────────────────────────────────────────────────────────────────────
  clearFavorites: () => {
    set({ favorites: [], loading: false, error: null, userId: null, orgId: null });
  },
}));
